<script lang="ts">
  /* eslint-disable svelte/no-navigation-without-resolve -- resolveServiceHref returns a resolved pathname. */
  import { onMount } from "svelte";
  import { resolve } from "$app/paths";
  import { resolveServiceHref } from "$lib/content/service-pages";
  import { ArrowUpRight } from "lucide-svelte";
  import { registerScrollTrigger, refreshScrollTriggers } from "$lib/animations/gsap";
  import { onLenisScroll } from "$lib/animations/lenis";
  import { showcaseProjects, workGalleryItems } from "$lib/content/home";
  import { getRemoteImageSrcset } from "$lib/utils/responsive-media";
  import { getHomeVideoPreview } from "$lib/content/home-video-previews";
  import { loadVideoSource } from "$lib/hooks/preload-video";
  import { _ } from "svelte-i18n";
  import { mediaUrl, getPreviewUrl } from "$lib/utils/media";
  import type {
    PreviewMedia,
    ShowcaseProjectMedia,
    WorkGalleryItem,
  } from "$lib/types/content";

  const finalShowcaseProject = showcaseProjects.at(-1);
  type WorkFieldSectionItem = Omit<WorkGalleryItem, "media"> & {
    media: ShowcaseProjectMedia;
  };

  const workFieldPortraitMedia: Record<string, PreviewMedia> = {
    "fashion-color": {
      src: mediaUrl("/images/home/home-showcase-wireframe-clay-sneaker-3d.png"),
      alt: "Wireframe clay 3D render of a sneaker",
      width: 1122,
      height: 1402,
      credit: "Studio Click House",
    },
    "jewelry-detail": {
      src: mediaUrl("/images/home/home-showcase-jewelry-statement-gold-earrings.webp"),
      alt: "Pair of sculptural gold earrings with a polished finish",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
    "shadow-study": {
      src: mediaUrl("/images/home/home-showcase-luxury-metallic-gold-bag.webp"),
      alt: "Metallic gold handbag photographed against a clean studio background",
      width: 1600,
      height: 2000,
      credit: "Studio Click House",
    },
  };
  const withImageKind = (item: WorkGalleryItem): WorkFieldSectionItem => ({
    ...item,
    media: {
      ...(workFieldPortraitMedia[item.id] ?? item.media),
      kind: "image",
    },
  });
  const workFieldDemoItem: WorkFieldSectionItem = {
    id: "commercial-video-editing",
    category: "Commercial video editing",
    title: "Movement shaped for product and campaign stories.",
    description:
      "Commercial editing, pacing, color, and delivery for high-impact branded video campaigns.",
    tags: ["Video", "Editing"],
    media: {
      kind: "video",
      src: "/videos/video-editing-wall-resort-collection-film.mp4",
      alt: "Fashion e-commerce film production and commercial editing",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
    },
  };
  const workFieldLifestyleVideoItem: WorkFieldSectionItem = {
    id: "fashion-lifestyle-video",
    category: "Editorial fashion motion",
    title: "Cinematic pacing that elevates brand identity.",
    description:
      "High-end fashion editorial reels, lifestyle campaign cutdowns, and color grading tuned for digital screens.",
    tags: ["Fashion", "Motion"],
    media: {
      kind: "video",
      src: "/videos/video-editing-wall-activewear-studio-motion.mp4",
      alt: "Fashion lifestyle editorial video reel in natural sunlight",
      width: 1920,
      height: 1080,
      credit: "Studio Click House",
    },
  };
  const workFieldGalleryItems = [
    withImageKind(workGalleryItems[0]),
    workFieldDemoItem,
    withImageKind(workGalleryItems[1]),
    workFieldLifestyleVideoItem,
    ...workGalleryItems.slice(2).map(withImageKind),
  ];
  const workFieldServiceLabels: Record<string, string> = {
    "commercial-video-editing": "Commercial video editing",
    "fashion-lifestyle-video": "Fashion video editing",
    "fashion-color": "Fashion color correction",
    "jewelry-detail": "Jewelry retouching",
    "shadow-study": "Product image composition",
  };
  const workFieldServiceSlugs: Record<string, string> = {
    "commercial-video-editing": "commercial-editing",
    "fashion-lifestyle-video": "commercial-editing",
    "fashion-color": "color-correction",
    "jewelry-detail": "jewelry-retouching",
    "shadow-study": "ecommerce-retouching",
  };
  const workFieldItems = finalShowcaseProject
    ? [
        {
          ...workGalleryItems[0],
          id: `showcase-handoff-${finalShowcaseProject.id}`,
          title: finalShowcaseProject.title,
          category: finalShowcaseProject.category,
          description: finalShowcaseProject.description,
          tags: ["3D", "CGI"],
          media: finalShowcaseProject.media,
        },
        ...workFieldGalleryItems,
      ]
    : workFieldGalleryItems;

  let section: HTMLElement | null = null;
  let stage: HTMLElement | null = null;
  let workFieldsTrack: HTMLElement | null = null;

  onMount(() => {
    let active = true;
    let context: { revert: () => void } | undefined;
    let videoObserver: IntersectionObserver | undefined;
    const stageVideos = Array.from(
      stage?.querySelectorAll<HTMLVideoElement>("video") ?? [],
    );

    const projectVideos = stageVideos;

    const videoCleanupFns: Array<() => void> = [];
    stageVideos.forEach((v) => {
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
    });

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    let isSectionNearViewport = false;
    let currentPlayingVideo: HTMLVideoElement | null = null;
    let isRafScheduled = false;
    let videoUpdateFrame = 0;
    const shouldPlayVideo = (video: HTMLVideoElement) =>
      active &&
      !document.hidden &&
      !prefersReducedMotion.matches &&
      isSectionNearViewport &&
      currentPlayingVideo === video;

    // Track in-flight play promises to strictly prevent DOMException AbortError
    // when pause() is requested before play() fulfills
    const playPromises = new WeakMap<HTMLVideoElement, Promise<void>>();

    const safePlay = (video: HTMLVideoElement) => {
      loadVideoSource(video);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      if (!video.paused) return;
      if (playPromises.has(video)) return;

      try {
        const promise = video.play();
        if (promise !== undefined) {
          playPromises.set(video, promise);
          promise
            .catch(() => {})
            .finally(() => {
              playPromises.delete(video);
              if (!shouldPlayVideo(video) && !video.paused) {
                video.pause();
              }
            });
        }
      } catch {
        // Safe catch for environment restrictions
      }
    };

    const safePause = (video: HTMLVideoElement) => {
      const pending = playPromises.get(video);
      if (pending) {
        pending
          .then(() => {
            if (!shouldPlayVideo(video) && !video.paused) {
              video.pause();
            }
          })
          .catch(() => {});
      } else {
        video.pause();
      }
    };

    const pauseAllStageVideos = () => {
      currentPlayingVideo = null;
      stageVideos.forEach((video) => {
        safePause(video);
      });
    };

    // These per-frame DOM caches do not drive Svelte rendering.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const frameStyles = new Map<HTMLElement, CSSStyleDeclaration>();
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const frameBounds = new Map<HTMLElement, DOMRect>();
    const getFrameStyle = (element: HTMLElement) => {
      const style = frameStyles.get(element) ?? window.getComputedStyle(element);
      frameStyles.set(element, style);
      return style;
    };
    const getFrameBounds = (element: HTMLElement) => {
      const bounds = frameBounds.get(element) ?? element.getBoundingClientRect();
      frameBounds.set(element, bounds);
      return bounds;
    };
    const isElementDomVisible = (el: HTMLElement): boolean => {
      if (!el.isConnected) return false;
      let current: HTMLElement | null = el;
      while (current && current !== stage && current !== document.body) {
        const style = getFrameStyle(current);
        if (
          style.display === "none" ||
          style.visibility === "hidden" ||
          parseFloat(style.opacity || "1") < 0.05
        ) {
          return false;
        }
        current = current.parentElement;
      }
      return true;
    };

    const getVideoFocusScore = (
      video: HTMLVideoElement,
    ): { video: HTMLVideoElement; score: number } | null => {
      if (!isElementDomVisible(video)) return null;

      const rect = getFrameBounds(video);
      if (rect.width <= 0 || rect.height <= 0) return null;

      let vLeft = Math.max(0, rect.left);
      let vRight = Math.min(window.innerWidth, rect.right);
      let vTop = Math.max(0, rect.top);
      let vBottom = Math.min(window.innerHeight, rect.bottom);
      let parent = video.parentElement;
      while (parent) {
        const style = getFrameStyle(parent);
        const bounds = getFrameBounds(parent);
        if (/(hidden|clip|auto|scroll)/.test(style.overflowX)) {
          vLeft = Math.max(vLeft, bounds.left);
          vRight = Math.min(vRight, bounds.right);
        }
        if (/(hidden|clip|auto|scroll)/.test(style.overflowY)) {
          vTop = Math.max(vTop, bounds.top);
          vBottom = Math.min(vBottom, bounds.bottom);
        }
        parent = parent.parentElement;
      }

      const vWidth = Math.max(0, vRight - vLeft);
      const vHeight = Math.max(0, vBottom - vTop);
      const visibleArea = vWidth * vHeight;

      if (vWidth < 60 || vHeight < 60 || visibleArea < 15000) {
        return null;
      }

      const card = video.closest(
        ".project-panel, .work-field-slide, .work-card",
      );
      if (!card) return null;
      let visibleSamples = 0;
      for (const x of [0.15, 0.5, 0.85]) {
        for (const y of [0.15, 0.5, 0.85]) {
          const hit = document.elementFromPoint(
            vLeft + vWidth * x,
            vTop + vHeight * y,
          );
          if (hit && card.contains(hit)) visibleSamples += 1;
        }
      }
      if (visibleSamples === 0) return null;

      const centerX = (vLeft + vRight) * 0.5;
      const centerY = (vTop + vBottom) * 0.5;

      const dx =
        (centerX - window.innerWidth * 0.5) / (window.innerWidth * 0.5);
      const dy =
        (centerY - window.innerHeight * 0.5) / (window.innerHeight * 0.5);
      const distFromCenter = Math.hypot(dx, dy);

      // Score prioritizes cards with high visibility and proximity to viewport center
      const score =
        (visibleArea * visibleSamples) / 9 / (1 + distFromCenter * 1.4);

      return { video, score };
    };

    const getFocusedVideo = (): HTMLVideoElement | null => {
      const candidates: Array<{ video: HTMLVideoElement; score: number }> = [];

      for (const video of projectVideos) {
        const res = getVideoFocusScore(video);
        if (res && res.score > 0.05) {
          candidates.push(res);
        }
      }

      if (candidates.length === 0) return null;

      candidates.sort((a, b) => b.score - a.score);
      const topCandidate = candidates[0];

      if (currentPlayingVideo) {
        const currentCandidate = candidates.find(
          (c) => c.video === currentPlayingVideo,
        );
        // Retain current playing video if it remains close to top score to prevent micro-jitter
        if (
          currentCandidate &&
          currentCandidate.score >= topCandidate.score * 0.82
        ) {
          return currentPlayingVideo;
        }
      }

      return topCandidate.video;
    };

    const updateFocusedVideo = () => {
      isRafScheduled = false;
      frameStyles.clear();
      frameBounds.clear();
      if (
        !active ||
        document.hidden ||
        prefersReducedMotion.matches ||
        !isSectionNearViewport
      ) {
        pauseAllStageVideos();
        return;
      }

      currentPlayingVideo = getFocusedVideo();
      const upcomingVideos = stageVideos.filter((video) => {
        if (video.getAttribute("src") || !isElementDomVisible(video)) return false;
        const rect = getFrameBounds(video);
        return rect.width > 0 && rect.height > 0 &&
          rect.bottom > -window.innerHeight && rect.top < window.innerHeight * 2 &&
          rect.right > 0 && rect.left < window.innerWidth * 1.5;
      });
      upcomingVideos.forEach(loadVideoSource);

      projectVideos.forEach((video) => {
        if (video === currentPlayingVideo) {
          safePlay(video);
        } else {
          safePause(video);
        }
      });
    };

    const scheduleUpdateFocusedVideo = () => {
      if (!isSectionNearViewport) return;
      if (!isRafScheduled) {
        isRafScheduled = true;
        videoUpdateFrame = requestAnimationFrame(updateFocusedVideo);
      }
    };

    const handleMotionChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        pauseAllStageVideos();
      } else if (isSectionNearViewport) {
        scheduleUpdateFocusedVideo();
      }
    };
    prefersReducedMotion.addEventListener("change", handleMotionChange);

    stageVideos.forEach((video) => {
      video.addEventListener("canplay", scheduleUpdateFocusedVideo);
      videoCleanupFns.push(() => {
        video.removeEventListener("canplay", scheduleUpdateFocusedVideo);
      });
    });
    document.addEventListener("visibilitychange", scheduleUpdateFocusedVideo);

    const mobileTrack = stage?.querySelector<HTMLElement>(
      ".work-fields-mobile",
    );
    if (mobileTrack) {
      mobileTrack.addEventListener("scroll", scheduleUpdateFocusedVideo, {
        passive: true,
      });
    }

    const unregisterLenis = onLenisScroll(scheduleUpdateFocusedVideo);

    const gestureEvents = [
      "wheel",
      "touchstart",
      "pointerdown",
      "keydown",
    ] as const;
    const handleFirstGesture = () => {
      gestureEvents.forEach((ev) =>
        window.removeEventListener(ev, handleFirstGesture),
      );
      if (isSectionNearViewport && !prefersReducedMotion.matches) {
        scheduleUpdateFocusedVideo();
      }
    };
    gestureEvents.forEach((ev) =>
      window.addEventListener(ev, handleFirstGesture, { passive: true }),
    );

    window.addEventListener("scroll", scheduleUpdateFocusedVideo, {
      passive: true,
    });
    window.addEventListener("resize", scheduleUpdateFocusedVideo, {
      passive: true,
    });

    if (prefersReducedMotion.matches) {
      pauseAllStageVideos();
    } else if ("IntersectionObserver" in window && section) {
      videoObserver = new IntersectionObserver(
        ([entry]) => {
          isSectionNearViewport = Boolean(entry?.isIntersecting);
          if (isSectionNearViewport && !prefersReducedMotion.matches) {
            scheduleUpdateFocusedVideo();
          } else {
            pauseAllStageVideos();
          }
        },
        { rootMargin: `${window.innerHeight}px 0px`, threshold: 0 },
      );
      videoObserver.observe(section);
    } else {
      isSectionNearViewport = true;
      scheduleUpdateFocusedVideo();
    }

    registerScrollTrigger().then((runtime) => {
      if (!active || !runtime || !section || !stage) return;

      const localSection = section;
      const localStage = stage;
      const { gsap, ScrollTrigger } = runtime;

      context = gsap.context(() => {
        const media = gsap.matchMedia();

        media.add(
          "(min-width: 768px) and (prefers-reduced-motion: no-preference)",
          () => {
            const panels = gsap.utils.toArray<HTMLElement>(".project-panel");
            const bodies = gsap.utils.toArray<HTMLElement>(".project-body");
            const mediaReveals = gsap.utils.toArray<HTMLElement>(
              ".project-media-reveal",
            );
            const mediaContents = gsap.utils.toArray<HTMLElement>(
              ".project-media-content",
            );
            const images = gsap.utils.toArray<HTMLElement>(".project-image");
            const revealLines = panels.map((panel) =>
              gsap.utils.toArray<HTMLElement>(".detail-reveal-inner", panel),
            );
            const projectLinks =
              gsap.utils.toArray<HTMLElement>(".project-link");
            const localWorkFieldsStage =
              localStage.querySelector<HTMLElement>(".work-fields-track");
            const workFieldsMediaTrack = localStage.querySelector<HTMLElement>(
              ".work-fields-media-track",
            );
            const workFieldsMediaViewport =
              localStage.querySelector<HTMLElement>(
                ".work-fields-media-viewport",
              );
            const workFieldsMediaContent =
              localStage.querySelector<HTMLElement>(
                ".work-fields-media-content",
              );
            const workFieldsMediaExitWash =
              localStage.querySelector<HTMLElement>(
                ".work-fields-media-exit-wash",
              );
            const workFieldsCopy =
              localStage.querySelector<HTMLElement>(".work-fields-copy");
            const workFieldsIntroPanel = localStage.querySelector<HTMLElement>(
              ".work-fields-intro-panel",
            );
            const workFieldsIntroLines = gsap.utils.toArray<HTMLElement>(
              ".work-fields-intro-inner",
            );
            const handoffMedia = localStage.querySelector<HTMLElement>(
              ".work-field-handoff-media",
            );
            const handoffSlide = localStage.querySelector<HTMLElement>(
              ".work-field-handoff-slide",
            );
            const handoffDetails = localStage.querySelector<HTMLElement>(
              ".work-field-handoff-details",
            );
            const handoffCounter = localStage.querySelector<HTMLElement>(
              ".work-field-handoff-counter",
            );
            const workFieldImages =
              gsap.utils.toArray<HTMLElement>(".work-field-image");
            const workFieldProgressItems = gsap.utils.toArray<HTMLElement>(
              ".work-field-progress-item",
            );

            if (
              panels.length === 0 ||
              !localWorkFieldsStage ||
              !workFieldsMediaTrack ||
              !workFieldsMediaViewport ||
              !workFieldsMediaContent ||
              !workFieldsMediaExitWash ||
              !workFieldsCopy ||
              !workFieldsIntroPanel ||
              !handoffMedia ||
              !handoffSlide ||
              !handoffDetails ||
              !handoffCounter ||
              workFieldProgressItems.length === 0
            ) {
              return;
            }

            const viewportWidth = () => window.innerWidth;
            const stageHeight = () => localStage.clientHeight;
            const workFieldCardRatio = 0.9;
            const workFieldTrackSteps = Math.max(
              1,
              workFieldItems.length - 1 / workFieldCardRatio,
            );
            const workFieldTrackStepDuration = 0.42;
            const workFieldTrackDuration =
              workFieldTrackSteps * workFieldTrackStepDuration;
            const firstWorkFieldProgressTime = 1.05;
            const lastWorkFieldProgressTime =
              0.9 + workFieldTrackDuration - 0.2;
            const workFieldProgressInterval =
              (lastWorkFieldProgressTime - firstWorkFieldProgressTime) /
              Math.max(1, workFieldGalleryItems.length - 1);
            const projectTransitionDuration = 0.82;
            const initialPanelPositions = [0, 0.5, 0.83, 0.93];
            const initialBodyOffsets = [0, 0.52, 0.62];

            ScrollTrigger.getById("horizontal-projects-entry")?.kill(true);
            ScrollTrigger.getById("horizontal-projects-handoff")?.kill(true);
            ScrollTrigger.getById("horizontal-projects-pin")?.kill(true);

            panels.forEach((panel, index) => {
              gsap.set(panel, {
                x: () =>
                  viewportWidth() * (initialPanelPositions[index] ?? 1),
                zIndex: index + 2,
                force3D: true,
              });
              gsap.set(bodies[index], {
                y: () => stageHeight() * (initialBodyOffsets[index] ?? 0.8),
                force3D: true,
              });
              gsap.set(images[index], {
                scale: 1,
                transformOrigin: "center center",
                force3D: true,
              });
              gsap.set(mediaReveals[index], {
                scaleY: index === 0 ? 1 : 0.68,
                transformOrigin: "top center",
                force3D: true,
              });
              gsap.set(mediaContents[index], {
                scaleY: index === 0 ? 1 : 1 / 0.68,
                transformOrigin: "top center",
                force3D: true,
              });
              gsap.set(revealLines[index], {
                yPercent: index === 0 ? 0 : 115,
                autoAlpha: index === 0 ? 1 : 0,
                force3D: true,
              });
              gsap.set(projectLinks[index], {
                scale: index === 0 ? 1 : 0,
                autoAlpha: index === 0 ? 1 : 0,
              });
            });

            gsap.set(panels, {
              yPercent: 0,
              autoAlpha: 1,
            });
            gsap.set(localWorkFieldsStage, {
              x: 0,
              autoAlpha: 0,
              force3D: true,
            });
            gsap.set(workFieldsMediaTrack, {
              y: 0,
              width: "100%",
              force3D: true,
            });
            gsap.set(workFieldsMediaViewport, {
              scaleX: 1,
              transformOrigin: "left center",
              force3D: true,
            });
            gsap.set(workFieldsMediaContent, {
              scaleX: 1,
              transformOrigin: "left center",
              force3D: true,
            });
            gsap.set(workFieldsMediaExitWash, { autoAlpha: 0 });
            gsap.set(handoffDetails, {
              top: "0%",
              y: 0,
              autoAlpha: 1,
              force3D: true,
            });
            gsap.set(workFieldsCopy, {
              x: () => viewportWidth() / 3,
              force3D: true,
            });
            gsap.set(workFieldImages, {
              scale: 1,
              transformOrigin: "center center",
              force3D: true,
            });
            gsap.set(
              workFieldImages.filter((image) =>
                image.classList.contains("work-field-shadow-image"),
              ),
              {
                scale: 1.14,
                transformOrigin: "center top",
                force3D: true,
              },
            );
            gsap.set(workFieldsIntroPanel, { autoAlpha: 1 });
            gsap.set(workFieldsIntroLines, {
              yPercent: 115,
              autoAlpha: 0,
              force3D: true,
            });
            gsap.set(workFieldProgressItems, {
              y: 12,
              autoAlpha: 0,
              force3D: true,
            });
            const timeline = gsap.timeline({
              defaults: { duration: 1, ease: "none" },
              scrollTrigger: {
                id: "horizontal-projects-pin",
                trigger: localSection,
                start: "top top+=70",
                end: () =>
                  `+=${stageHeight() * (2.98 + Math.max(0, panels.length - 3) * projectTransitionDuration + workFieldTrackSteps * workFieldCardRatio)}`,
                pin: localStage,
                pinSpacing: true,
                scrub: true,
                refreshPriority: 100,
                invalidateOnRefresh: true,
                onUpdate: scheduleUpdateFocusedVideo,
              },
            });

            const revealProject = (
              index: number,
              position: number | string,
            ) => {
              const offset = (value: number) => {
                return typeof position === "number"
                  ? position + value
                  : String(position) + "+=" + value;
              };

              timeline
                .to(
                  bodies[index],
                  { y: 0, duration: 0.64, ease: "power2.out" },
                  offset(0.06),
                )
                .to(
                  mediaReveals[index],
                  { scaleY: 1, duration: 0.62, ease: "power2.out" },
                  offset(0.08),
                )
                .to(
                  mediaContents[index],
                  { scaleY: 1, duration: 0.62, ease: "power2.out" },
                  offset(0.08),
                )
                .to(
                  images[index],
                  { scale: 1, duration: 0.72, ease: "power2.out" },
                  offset(0.08),
                )
                .to(
                  revealLines[index],
                  {
                    yPercent: 0,
                    autoAlpha: 1,
                    stagger: 0.02,
                    duration: 0.3,
                    ease: "power3.out",
                  },
                  offset(0.28),
                )
                .to(
                  projectLinks[index],
                  {
                    scale: 1,
                    autoAlpha: 1,
                    duration: 0.2,
                    ease: "back.out(1.5)",
                  },
                  offset(0.16),
                );
            };

            let previousProjectLabel = "firstProject";
            timeline.addLabel(previousProjectLabel, 0);
            timeline.call(scheduleUpdateFocusedVideo, [], 0);

            for (let index = 1; index < panels.length; index += 1) {
              const projectLabel = "project" + (index + 1);
              const transitionDelay =
                index === 1 ? 0 : projectTransitionDuration;

              timeline
                .addLabel(
                  projectLabel,
                  previousProjectLabel + "+=" + transitionDelay,
                )
                .call(scheduleUpdateFocusedVideo, [], projectLabel)
                .to(
                  projectLinks[index - 1],
                  { scale: 0, autoAlpha: 0, duration: 0.15 },
                  projectLabel + "+=0.5",
                )
                .to(
                  panels[index - 1],
                  { x: () => viewportWidth() * (index === 1 ? -0.5 : -0.666) },
                  projectLabel,
                )
                .to(panels[index], { x: 0 }, projectLabel);

              if (panels[index + 1]) {
                timeline.to(
                  panels[index + 1],
                  { x: () => viewportWidth() * 0.666 },
                  projectLabel,
                );
              }

              revealProject(index, projectLabel);
              previousProjectLabel = projectLabel;
            }

            const lastProjectIndex = panels.length - 1;

            timeline
              .addLabel("workFields", previousProjectLabel + "+=1.05")
              .call(scheduleUpdateFocusedVideo, [], "workFields")
              .to(
                projectLinks[lastProjectIndex],
                { scale: 0, autoAlpha: 0, duration: 0.15 },
                "workFields",
              )
              .set(panels[lastProjectIndex], { autoAlpha: 0 }, "workFields")
              .set(
                localWorkFieldsStage,
                {
                  x: 0,
                  autoAlpha: 1,
                  force3D: true,
                },
                "workFields",
              )
              .to(
                handoffDetails,
                {
                  top: "100%",
                  paddingTop: 0,
                  paddingBottom: 0,
                  duration: 0.9,
                  ease: "none",
                },
                "workFields",
              )
              .to(
                handoffCounter,
                {
                  autoAlpha: 0,
                  duration: 0.24,
                  ease: "none",
                },
                "workFields",
              )
              .to(
                handoffSlide,
                {
                  height: `${workFieldCardRatio * 100}%`,
                  duration: 0.9,
                  ease: "none",
                },
                "workFields",
              )
              .to(
                handoffMedia,
                {
                  height: "100%",
                  duration: 0.9,
                  ease: "none",
                },
                "workFields",
              )
              .to(
                workFieldsIntroLines,
                {
                  yPercent: 0,
                  autoAlpha: 1,
                  stagger: 0.03,
                  duration: 0.4,
                  ease: "power3.out",
                },
                "workFields+=0.08",
              )
              .to(
                workFieldsMediaTrack,
                {
                  width: "50%",
                  duration: 0.9,
                  ease: "none",
                },
                "workFields",
              )
              .to(
                workFieldsMediaTrack,
                {
                  y: () =>
                    -stageHeight() * workFieldCardRatio * workFieldTrackSteps,
                  duration: workFieldTrackDuration,
                  ease: "none",
                  force3D: true,
                },
                "workFields+=0.9",
              )
              .to(
                workFieldsMediaViewport,
                {
                  scaleX: 0.5,
                  duration: 0.9,
                  ease: "none",
                  force3D: true,
                },
                "workFields",
              )
              .to(
                workFieldsMediaContent,
                {
                  scaleX: 2,
                  duration: 0.9,
                  ease: "none",
                  force3D: true,
                },
                "workFields",
              )
              .to(
                workFieldsCopy,
                {
                  x: 0,
                  duration: 0.9,
                  ease: "none",
                  force3D: true,
                },
                "workFields",
              )
              .to(
                workFieldsMediaExitWash,
                {
                  autoAlpha: 1,
                  duration: 0.18,
                  ease: "none",
                },
                `workFields+=${0.72 + workFieldTrackDuration}`,
              );

            for (
              let index = 0;
              index < workFieldGalleryItems.length;
              index += 1
            ) {
              const fieldLabel = `workFieldProgress${index + 1}`;
              const fieldTime =
                firstWorkFieldProgressTime + index * workFieldProgressInterval;

              timeline.addLabel(fieldLabel, `workFields+=${fieldTime}`).to(
                workFieldProgressItems[index],
                {
                  y: 0,
                  autoAlpha: 1,
                  duration: 0.24,
                  ease: "power3.out",
                },
                fieldLabel,
              );
            }

            return;
          },
        );
      }, localSection);

      void refreshScrollTriggers();
    });

    return () => {
      active = false;
      pauseAllStageVideos();
      cancelAnimationFrame(videoUpdateFrame);
      videoCleanupFns.forEach((fn) => fn());
      document.removeEventListener(
        "visibilitychange",
        scheduleUpdateFocusedVideo,
      );
      gestureEvents.forEach((ev) =>
        window.removeEventListener(ev, handleFirstGesture),
      );
      unregisterLenis();
      mobileTrack?.removeEventListener("scroll", scheduleUpdateFocusedVideo);
      window.removeEventListener("scroll", scheduleUpdateFocusedVideo);
      window.removeEventListener("resize", scheduleUpdateFocusedVideo);
      prefersReducedMotion.removeEventListener("change", handleMotionChange);
      videoObserver?.disconnect();
      context?.revert();
    };
  });
</script>

<section
  id="horizontal-projects-showcase"
  bind:this={section}
  aria-labelledby="showcase-title"
  class="relative isolate bg-brand-dark"
>
  <h2 id="showcase-title" class="sr-only">Our services</h2>

  <div
    bind:this={stage}
    class="project-stage relative h-[calc(100dvh_-_4.35rem)] overflow-hidden bg-brand-light"
  >
    {#each showcaseProjects as project, index (project.id)}
      <article
        class="project-panel absolute inset-y-0 left-0 overflow-hidden border-l border-brand-dark/15 bg-brand-dark {index === 0 ? 'w-[50vw]' : 'w-[66.6vw]'}"
        aria-labelledby="project-title-{project.id}"
      >
        <div class="project-body relative z-[1] h-full w-full">
          <div class="project-media absolute inset-0 overflow-hidden">
            <div
              class="project-media-reveal absolute inset-0 origin-top overflow-hidden"
            >
              <div
                class="project-media-content relative h-full w-full origin-top"
              >
                {#if project.media.kind === "video"}
                  <video
                    data-src={getHomeVideoPreview(project.media.src).src}
                    poster={getHomeVideoPreview(project.media.src).poster ?? project.media.poster}
                    width={project.media.width}
                    height={project.media.height}
                    muted
                    loop
                    playsinline
                    preload="none"
                    aria-label={project.media.alt}
                    class="project-image h-full w-full object-cover"
                    style:object-position={project.media.objectPosition ||
                      "center"}
                  ></video>
                {:else}
                  <img
                    src={getPreviewUrl(project.media.src)}
                    srcset={getRemoteImageSrcset(project.media.src)}
                    sizes="67vw"
                    alt={project.media.alt}
                    width={project.media.width}
                    height={project.media.height}
                    loading="lazy"
                    class="project-image h-full w-full object-cover"
                    style:object-position={project.media.objectPosition ||
                      "center"}
                  />
                {/if}
              </div>
            </div>
          </div>

          <div
            class="project-details absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-brand-dark/95 via-brand-dark/35 to-transparent px-[clamp(1.25rem,3vw,3rem)] pb-[clamp(1.5rem,3vw,3rem)] pt-24 text-brand-light"
          >
            <div class="mb-4 flex items-center gap-4">
              <span
                class="detail-reveal rounded-full border border-brand-light/60 px-3 py-1 font-sans text-xs font-medium"
                ><span class="detail-reveal-inner block">{project.year}</span
                ></span
              >
              <p
                class="detail-reveal font-sans text-xs font-medium tracking-[0.01em] text-brand-light/60"
              >
                <span class="detail-reveal-inner block"
                  >{$_(`home.showcaseProjects.${project.id}.category`) ||
                    project.category}</span
                >
              </p>
            </div>
            <h3
              id="project-title-{project.id}"
              class="detail-reveal max-w-[12ch] pb-1 font-sans text-[clamp(2.8rem,5.4vw,5.5rem)] font-semibold leading-[1.02] tracking-[-0.045em]"
            >
              <span class="detail-reveal-inner block"
                >{$_(`home.showcaseProjects.${project.id}.title`) ||
                  project.title}</span
              >
            </h3>
            <p
              class="detail-reveal mt-4 max-w-[48ch] text-[clamp(0.95rem,1.35vw,1.25rem)] font-normal leading-[1.55] text-brand-light/90"
            >
              <span class="detail-reveal-inner block"
                >{$_(`home.showcaseProjects.${project.id}.description`) ||
                  project.description}</span
              >
            </p>
            <ul
              class="mt-5 flex flex-wrap gap-x-5 gap-y-2"
              aria-label="Service capabilities"
            >
              {#each project.capabilities as capability (capability)}
                <li
                  class="detail-reveal inline-flex items-center gap-2 font-sans text-xs font-medium tracking-[0.01em] text-brand-light/80"
                >
                  <span
                    class="detail-reveal-inner inline-flex items-center gap-2"
                    ><span
                      class="size-2 rounded-full bg-brand-green"
                      aria-hidden="true"
                    ></span>{capability}</span
                  >
                </li>
              {/each}
            </ul>
            <a
              href={resolve(project.href as "/services")}
              class="group project-link mt-6 inline-flex w-fit items-center gap-3 border-b-2 border-brand-green pb-2 font-sans text-sm font-medium tracking-[0.01em] text-brand-light focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-green"
              aria-label="View capabilities for {project.title}"
            >
              {$_("services.hero.viewCapabilities") || "View capabilities"}
              <ArrowUpRight
                class="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </div>
      </article>
    {/each}

    <div
      id="work-fields-rail"
      bind:this={workFieldsTrack}
      class="work-fields-track absolute inset-0 z-20 overflow-hidden bg-brand-light"
      aria-labelledby="work-fields-rail-title"
    >
      <h2 id="work-fields-rail-title" class="sr-only">
        Visual production fields
      </h2>

      <div class="work-fields-desktop relative h-full w-full">
        <div
          class="work-fields-media-viewport absolute inset-y-0 left-0 h-full w-[66.6%] overflow-hidden bg-brand-dark"
        >
          <div class="work-fields-media-content h-full w-full">
            <div class="work-fields-media-track h-full">
              {#each workFieldItems as item, index (item.id)}
                <figure
                  class:work-field-handoff-slide={index === 0}
                  class="work-field-slide relative overflow-hidden"
                >
                  {#if index === 0 && finalShowcaseProject}
                    <div
                      class="work-field-handoff-media relative h-full overflow-hidden bg-brand-dark"
                    >
                      {#if item.media.kind === "video"}
                        <video
                          data-src={getHomeVideoPreview(item.media.src).src}
                          poster={getHomeVideoPreview(item.media.src).poster ?? item.media.poster}
                          width={item.media.width}
                          height={item.media.height}
                          muted
                          loop
                          playsinline
                          preload="none"
                          aria-label={item.media.alt}
                          class="work-field-image h-full w-full object-cover object-center"
                          style:object-position={item.media.objectPosition ||
                            "center"}
                        ></video>
                      {:else}
                        <img
                          src={getPreviewUrl(item.media.src)}
                          srcset={getRemoteImageSrcset(item.media.src)}
                          sizes="67vw"
                          alt=""
                          width={item.media.width}
                          height={item.media.height}
                          loading="lazy"
                          class="work-field-image h-full w-full object-cover object-center"
                          style:object-position={item.media.objectPosition ||
                            "center"}
                        />
                      {/if}
                      <div
                        class="work-field-handoff-counter hidden"
                        aria-hidden="true"
                      ></div>
                    </div>

                    <div
                      class="work-field-handoff-details absolute inset-0 z-[2] flex flex-col justify-end overflow-hidden bg-gradient-to-t from-brand-dark/95 via-brand-dark/35 to-transparent px-[clamp(1.25rem,3vw,3rem)] pb-[clamp(1.5rem,3vw,3rem)] pt-24 text-brand-light"
                    >
                      <div class="mb-4 flex items-center gap-4">
                        <span
                          class="rounded-full border border-brand-light/60 px-3 py-1 font-sans text-xs font-medium"
                        >
                          {finalShowcaseProject.year}
                        </span>
                        <p
                          class="font-sans text-xs font-medium tracking-[0.01em]"
                        >
                          {$_(
                            `home.showcaseProjects.${finalShowcaseProject.id}.category`,
                          ) || finalShowcaseProject.category}
                        </p>
                      </div>

                      <h3
                        class="max-w-[12ch] pb-1 font-sans text-[clamp(2.8rem,5.4vw,5.5rem)] font-semibold leading-[1.02] tracking-[-0.045em]"
                      >
                        {$_(
                          `home.showcaseProjects.${finalShowcaseProject.id}.title`,
                        ) || finalShowcaseProject.title}
                      </h3>
                      <p
                        class="mt-4 max-w-[48ch] text-[clamp(0.95rem,1.35vw,1.25rem)] font-normal leading-[1.55] text-brand-light/90"
                      >
                        {$_(
                          `home.showcaseProjects.${finalShowcaseProject.id}.description`,
                        ) || finalShowcaseProject.description}
                      </p>
                      <ul
                        class="mt-5 flex flex-wrap gap-x-5 gap-y-2"
                        aria-label="Service capabilities"
                      >
                        {#each finalShowcaseProject.capabilities as capability (capability)}
                          <li
                            class="inline-flex items-center gap-2 font-sans text-xs font-medium tracking-[0.01em] text-brand-light/80"
                          >
                            <span
                              class="size-2 rounded-full bg-brand-green"
                              aria-hidden="true"
                            ></span>{capability}
                          </li>
                        {/each}
                      </ul>
                      <a
                        href={resolve(finalShowcaseProject.href as "/services")}
                        class="mt-6 inline-flex w-fit items-center gap-3 border-b-2 border-brand-green pb-2 font-sans text-xs font-medium tracking-[0.01em] text-brand-light"
                      >
                        {$_("services.hero.viewCapabilities") ||
                          "View capabilities"}
                        <ArrowUpRight class="h-4 w-4" />
                      </a>
                    </div>
                  {:else}
                    <div class="work-field-image-shell h-full w-full">
                      {#if item.media.kind === "video"}
                        <video
                          data-src={getHomeVideoPreview(item.media.src).src}
                          poster={getHomeVideoPreview(item.media.src).poster ?? item.media.poster}
                          width={item.media.width}
                          height={item.media.height}
                          muted
                          loop
                          playsinline
                          preload="none"
                          aria-label={item.media.alt}
                          class="work-field-image h-full w-full object-cover"
                        ></video>
                      {:else}
                        <img
                          src={getPreviewUrl(item.media.src)}
                          srcset={getRemoteImageSrcset(item.media.src)}
                          sizes="67vw"
                          alt={item.media.alt}
                          width={item.media.width}
                          height={item.media.height}
                          loading="lazy"
                          class="work-field-image h-full w-full object-cover"
                          class:work-field-shadow-image={item.id ===
                            "shadow-study"}
                        />
                      {/if}
                    </div>
                    <div
                      class="work-field-hover-shade pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-transparent to-brand-dark/35 opacity-0"
                      aria-hidden="true"
                    ></div>
                    <div
                      class="work-field-hover-copy pointer-events-none absolute inset-0 text-brand-light"
                    >
                      <div
                        class="work-field-hover-detail absolute inset-x-0 top-0 flex items-center justify-between gap-5 px-[clamp(1rem,2vw,1.5rem)] py-[clamp(0.8rem,1.5vh,1.2rem)]"
                      >
                        <p
                          class="font-sans text-xs font-medium tracking-[0.01em]"
                        >
                          {$_(`home.workGalleryItems.${item.id}.category`) ||
                            item.category}
                        </p>
                        <span
                          class="rounded-full border border-brand-light/80 px-3 py-1 font-sans text-xs font-medium"
                        >
                          {String(index).padStart(2, "0")} / {String(
                            workFieldGalleryItems.length,
                          ).padStart(2, "0")}
                        </span>
                      </div>
                      <h3
                        class="work-field-hover-detail absolute bottom-[clamp(1rem,2vw,1.5rem)] left-[clamp(1rem,2vw,1.5rem)] max-w-[70%] font-sans text-[clamp(1.3rem,1.8vw,2rem)] font-medium leading-[1.1] tracking-[-0.035em]"
                      >
                        {$_(`home.workGalleryItems.${item.id}.title`) ||
                          item.title}
                      </h3>
                      <a
                        href={resolve("/services")}
                        class="work-field-slide-arrow pointer-events-auto absolute bottom-[clamp(0.75rem,1.5vw,1.25rem)] right-[clamp(0.75rem,1.5vw,1.25rem)] flex h-[clamp(3.5rem,4.6vw,4.5rem)] w-[clamp(3.5rem,4.6vw,4.5rem)] items-center justify-center rounded-full border border-brand-light text-brand-light focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-light"
                        aria-label="Explore {item.title}"
                      >
                        <span class="work-field-slide-arrow-icon">
                          <ArrowUpRight class="h-5 w-5" />
                        </span>
                      </a>
                    </div>
                  {/if}
                </figure>
              {/each}
            </div>
          </div>
          <div
            class="work-fields-media-exit-wash pointer-events-none absolute inset-x-0 bottom-0 z-30 h-[24%] bg-gradient-to-b from-transparent to-brand-light"
            aria-hidden="true"
          ></div>
        </div>

        <aside
          class="work-fields-copy absolute inset-y-0 left-[33.333%] right-0 flex h-full min-w-0 flex-col overflow-hidden bg-brand-light text-brand-dark"
          aria-label="Studio Click House image and video post-production services"
        >
          <div class="relative min-h-0 flex-1 overflow-hidden">
            <article
              class="work-fields-intro-panel absolute inset-0 flex items-center justify-center px-[clamp(1.5rem,4vw,5rem)] py-[clamp(1.5rem,4vh,3.5rem)]"
              aria-labelledby="work-fields-intro-title"
            >
              <div class="w-full max-w-[48rem]">
                <p
                  class="work-fields-intro-inner mb-5 font-sans text-xs font-medium tracking-[0.01em] text-brand-dark/50"
                >
                  Image and video post-production
                </p>
                <h3
                  id="work-fields-intro-title"
                  class="overflow-hidden pb-[0.12em] font-sans text-[clamp(2.8rem,4.7vw,5.8rem)] font-medium leading-[1.02] tracking-[-0.055em]"
                >
                  <span class="work-fields-intro-inner block">
                    Built around every final frame.
                  </span>
                </h3>
                <p
                  class="mt-[clamp(1.25rem,2.5vh,2rem)] max-w-[52ch] overflow-hidden text-[clamp(0.95rem,1.05vw,1.125rem)] leading-[1.6] text-brand-dark/70"
                >
                  <span class="work-fields-intro-inner block">
                    Studio Click House supports ecommerce and campaign
                    production with product, beauty, fashion and jewelry
                    retouching, color correction, CGI, and commercial video
                    editing.
                  </span>
                </p>

                <ol
                  class="mt-[clamp(1.5rem,3vh,2.5rem)]"
                  aria-label="Post-production capabilities revealed by scroll"
                >
                  {#each workFieldGalleryItems as item, index (item.id)}
                    {@const serviceSlug =
                      workFieldServiceSlugs[item.id] ?? "ecommerce-retouching"}
                    <li
                      class="work-field-progress-item border-t border-brand-dark/15 last:border-b"
                    >
                      <a
                        href={resolveServiceHref(serviceSlug)}
                        class="group grid grid-cols-[2.25rem_1fr_auto] items-center gap-3 py-[clamp(0.5rem,0.9vh,0.7rem)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-dark"
                        aria-label="Explore the {workFieldServiceLabels[
                          item.id
                        ] ?? item.category} service"
                      >
                        <span
                          class="font-sans text-xs font-medium tracking-[0.01em]"
                        >
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span
                          class="text-[clamp(0.95rem,1.05vw,1.125rem)] font-medium"
                        >
                          {workFieldServiceLabels[item.id] ?? item.category}
                        </span>
                        <span
                          class="grid h-8 w-8 place-items-center rounded-full border border-brand-dark/35 transition-colors duration-300 group-hover:border-brand-dark group-hover:bg-brand-dark group-hover:text-brand-light"
                        >
                          <ArrowUpRight
                            class="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                            aria-hidden="true"
                          />
                        </span>
                      </a>
                    </li>
                  {/each}
                </ol>
              </div>
            </article>
          </div>
        </aside>
      </div>

      <div class="work-fields-mobile h-full gap-3">
        {#each workFieldGalleryItems as item (item.id)}
          <article
            class="work-card relative h-full w-[calc(100vw-2rem)] shrink-0 snap-start overflow-hidden rounded-[2rem] border border-brand-light/10 bg-brand-light/5"
          >
            {#if item.media.kind === "video"}
              <video
                data-src={getHomeVideoPreview(item.media.src).src}
                poster={getHomeVideoPreview(item.media.src).poster ?? item.media.poster}
                width={item.media.width}
                height={item.media.height}
                muted
                loop
                playsinline
                preload="none"
                aria-label={item.media.alt}
                class="work-card-image h-full w-full object-cover"
              ></video>
            {:else}
              <img
                src={getPreviewUrl(item.media.src)}
                srcset={getRemoteImageSrcset(item.media.src)}
                sizes="calc(100vw - 2rem)"
                alt={item.media.alt}
                width={item.media.width}
                height={item.media.height}
                loading="lazy"
                class="work-card-image h-full w-full object-cover"
              />
            {/if}
            <div
              class="work-card-shade absolute inset-0"
              aria-hidden="true"
            ></div>
            <div class="work-card-copy absolute inset-x-0 bottom-0 p-5 sm:p-7">
              <div class="work-card-detail">
                <h3
                  class="max-w-sm font-sans text-2xl font-semibold leading-[1.1] tracking-[-0.025em] text-brand-light sm:text-3xl"
                >
                  {$_(`home.workGalleryItems.${item.id}.title`) || item.title}
                </h3>
                <p
                  class="mt-4 max-w-sm text-sm leading-relaxed text-brand-light/80"
                >
                  {$_(`home.workGalleryItems.${item.id}.description`) ||
                    item.description}
                </p>
              </div>
            </div>
          </article>
        {/each}
      </div>
    </div>
  </div>
</section>

<style>
  .project-stage {
    contain: layout paint;
  }

  .detail-reveal {
    overflow: hidden;
  }

  @media (min-width: 768px) and (prefers-reduced-motion: no-preference) {
    .work-fields-track {
      visibility: hidden;
      opacity: 0;
    }
  }

  @media (min-width: 768px) {
    .work-fields-track::after {
      position: absolute;
      z-index: 50;
      inset: 0;
      background: radial-gradient(
        ellipse 62% 100% at 50% 100%,
        color-mix(in srgb, var(--color-brand-green) 30%, transparent),
        transparent 68%
      );
      content: "";
      pointer-events: none;
    }
  }

  .work-fields-desktop {
    display: block;
  }

  .work-fields-mobile {
    display: none;
  }

  .work-field-slide {
    height: 90%;
  }

  .work-field-handoff-slide {
    height: 100%;
  }

  .work-field-hover-shade {
    transition: opacity 420ms ease;
  }

  .work-field-hover-detail {
    opacity: 0;
    transform: translateY(0.7rem);
    transition:
      opacity 360ms ease,
      transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .work-field-slide-arrow {
    background-color: transparent;
    opacity: 0;
    transform: scale(0.82);
    transition:
      opacity 260ms ease,
      transform 520ms cubic-bezier(0.22, 1, 0.36, 1),
      background-color 360ms ease,
      border-color 360ms ease,
      color 360ms ease;
  }

  .work-field-slide-arrow-icon {
    transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .work-field-slide:hover .work-field-hover-shade,
  .work-field-slide:focus-within .work-field-hover-shade {
    opacity: 1;
  }

  .work-field-slide:hover .work-field-hover-detail,
  .work-field-slide:focus-within .work-field-hover-detail {
    opacity: 1;
    transform: translateY(0);
  }

  .work-field-slide:hover .work-field-slide-arrow,
  .work-field-slide:focus-within .work-field-slide-arrow {
    border-color: var(--color-brand-light);
    background-color: var(--color-brand-light);
    color: var(--color-brand-dark);
    transform: scale(1);
    opacity: 1;
    transition-delay: 0ms, 0ms, 160ms, 160ms, 160ms;
  }

  .work-field-slide:hover .work-field-slide-arrow-icon,
  .work-field-slide:focus-within .work-field-slide-arrow-icon {
    transform: translate(0.12rem, -0.12rem);
  }

  .work-card-image {
    transition: transform 900ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .work-card-shade {
    background: linear-gradient(
      to top,
      rgba(18, 17, 17, 0.92),
      rgba(18, 17, 17, 0.3) 62%,
      transparent
    );
  }

  .work-card-detail {
    max-height: 22rem;
    transform: translateY(0.6rem);
    visibility: hidden;
    transition:
      transform 760ms cubic-bezier(0.22, 1, 0.36, 1),
      visibility 0s linear 760ms;
  }

  .work-card-detail > * {
    opacity: 0;
    transform: translateY(0.7rem);
    transition:
      opacity 520ms ease,
      transform 680ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .work-card-detail > p {
    transition-delay: 120ms;
  }

  .work-card:hover .work-card-image {
    transform: scale(1.06);
  }

  .work-card:hover .work-card-detail {
    transform: translateY(0);
    visibility: visible;
    transition-delay: 0s;
  }

  .work-card:hover .work-card-detail > * {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 767px) {
    #horizontal-projects-showcase {
      overflow: hidden;
      padding-block: 1rem;
    }

    .project-stage {
      display: flex;
      flex-direction: column;
      height: auto;
      width: 100%;
      gap: 0.75rem;
      overflow: hidden;
      padding-inline: 1rem;
      scrollbar-width: none;
    }

    .project-stage::-webkit-scrollbar {
      display: none;
    }

    .project-panel {
      position: relative;
      inset: auto;
      width: 100%;
      min-width: 0;
      height: 82dvh;
      transform: none !important;
    }

    .work-fields-track {
      position: relative;
      inset: auto;
      z-index: auto;
      height: auto;
      width: 100%;
      transform: none !important;
      will-change: auto;
    }

    .work-fields-desktop {
      display: none;
    }

    .work-fields-mobile {
      display: grid;
    }

    .work-card {
      width: 100%;
      min-height: 82dvh;
    }

    .project-body {
      transform: none !important;
    }

    .project-details {
      min-height: 30dvh;
      transform: none !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    .detail-reveal-inner,
    .project-link {
      transform: none !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    .project-details > :first-child {
      grid-template-columns: auto 1fr;
    }

    .project-details > :first-child p:last-child {
      display: none;
    }

    .work-card-detail {
      transform: none;
      visibility: visible;
    }

    .work-card-detail > * {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) and (min-width: 768px) {
    .project-stage {
      display: flex;
      width: 100%;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
    }

    .project-panel {
      position: relative;
      inset: auto;
      flex: 0 0 82vw;
      width: 82vw;
      transform: none !important;
      scroll-snap-align: start;
    }

    .work-fields-track {
      position: relative;
      inset: auto;
      z-index: auto;
      height: 100%;
      width: max-content;
      flex: 0 0 auto;
      transform: none !important;
      will-change: auto;
    }

    .work-fields-desktop {
      display: none;
    }

    .work-fields-mobile {
      display: flex;
    }

    .work-card {
      width: 82vw;
      scroll-snap-align: start;
    }

    .project-body,
    .project-details,
    .project-image {
      transform: none !important;
      opacity: 1 !important;
      visibility: visible !important;
    }

    .detail-reveal-inner,
    .project-link {
      transform: none !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
  }

  @media (hover: none), (prefers-reduced-motion: reduce) {
    .work-field-hover-shade {
      opacity: 1;
      transition: none;
    }

    .work-field-hover-detail {
      opacity: 1;
      transform: none;
      transition: none;
    }

    .work-field-slide-arrow {
      border-color: var(--color-brand-light);
      background-color: var(--color-brand-light);
      color: var(--color-brand-dark);
      transform: scale(1);
      opacity: 1;
      transition: none;
    }

    .work-card-detail {
      transform: none;
      visibility: visible;
      transition: none;
    }

    .work-card-detail > * {
      opacity: 1;
      transform: none;
      transition: none;
    }

    .work-card-image {
      transition: none;
    }
  }
</style>
